import React, { useEffect, useState } from "react";
import { CreditCard, Loader2, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtCAD, fmtDate } from "@/lib/billing";
import { PlanFields, FxAndMarkupFields, PacksEditor, ReceiptFields, TaxFields } from "./BillingSettingsSections";
import WebhookHealthBadge from "./WebhookHealthBadge";

const pick = (d) => ({
  plan_price_cad: d.plan_price_cad, included_enrichment: d.included_enrichment, included_scrape: d.included_scrape,
  swap_rate: d.swap_rate, usd_cad_rate: d.usd_cad_rate, usd_cad_rate_auto: d.usd_cad_rate_auto !== false,
  topup_markup_pct: d.topup_markup_pct ?? 10, complimentary_days: d.complimentary_days,
  packs: (d.packs || []).map((p) => ({ ...p })),
  receipt: { ...(d.receipt || {}) },
  tax: { gst_registered: !!d.tax?.gst_registered, gst_registered_from: d.tax?.gst_registered_from || "", threshold_cad: d.tax?.threshold_cad, pst_sk_rate: d.tax?.pst_sk_rate },
});

const num = (v, d = 0) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? d : Number(v));

export default function BillingSettingsCard() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmLaunch, setConfirmLaunch] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing/settings");
      setS(data);
      setForm(pick(data));
    } catch (e) {
      toast.error("Failed to load billing settings", { description: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch, key = "save") => {
    setBusy(key);
    try {
      const { data } = await api.put("/admin/billing/settings", patch);
      setS(data);
      setForm(pick(data));
      toast.success("Billing settings saved");
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  const saveForm = () => save({
    plan_price_cad: num(form.plan_price_cad, 50), included_enrichment: Math.round(num(form.included_enrichment)),
    included_scrape: Math.round(num(form.included_scrape)), swap_rate: num(form.swap_rate, 1),
    usd_cad_rate_auto: !!form.usd_cad_rate_auto,
    ...(form.usd_cad_rate_auto ? {} : { usd_cad_rate: num(form.usd_cad_rate, 1.37) }),
    topup_markup_pct: num(form.topup_markup_pct, 10),
    complimentary_days: Math.round(num(form.complimentary_days, 30)),
    packs: form.packs.map((p) => ({ id: p.id, pool: p.pool, label: p.label, credits: Math.round(num(p.credits)) })),
    receipt: form.receipt,
    tax: { ...form.tax, gst_registered_from: form.tax.gst_registered_from || null, threshold_cad: num(form.tax.threshold_cad, 30000), pst_sk_rate: num(form.tax.pst_sk_rate, 0.06) },
  });

  const refreshFx = async () => {
    setBusy("fx");
    try {
      const { data } = await api.post("/admin/billing/fx-refresh");
      setS(data.settings);
      setForm({ ...form, usd_cad_rate: data.settings.usd_cad_rate });
      toast.success(`USD→CAD ${data.rate} (Bank of Canada, ${data.date})`);
    } catch (e) {
      toast.error("Rate refresh failed", { description: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  const toggleEnabled = (v) => {
    if (v && !s.launch_date) { setConfirmLaunch(true); return; }
    save({ billing_enabled: v }, "toggle");
  };

  const runRenewals = async () => {
    setBusy("renewals");
    try {
      const { data } = await api.post("/admin/billing/run-renewals");
      toast.success(`Renewals run: ${data.charged} charged · ${data.failed} failed · ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error("Renewal run failed", { description: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  if (!s || !form) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="billing-settings-section">
        <div className="text-sm text-slate-500">Loading billing settings…</div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-6" data-testid="billing-settings-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 max-w-xl">
          <div className="rounded-md bg-slate-100 p-2 shrink-0"><CreditCard className="h-4 w-4 text-slate-600" /></div>
          <div>
            <h2 className="text-base font-semibold text-slate-900 font-display">Billing</h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Member subscriptions, included credits, top-up packs, taxes and receipts. Admin accounts are never billed.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm" data-testid="billing-enabled-label">
          <Switch checked={!!s.billing_enabled} onCheckedChange={toggleEnabled} disabled={busy === "toggle"} data-testid="billing-enabled-switch" />
          <span className={s.billing_enabled ? "font-medium text-emerald-700" : "text-slate-600"}>{s.billing_enabled ? "Billing is LIVE" : "Billing off (everything free)"}</span>
        </label>
      </div>

      <WebhookHealthBadge settings={s} />

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="billing-status-grid">
        <Info label="Launch date" value={s.launch_date ? fmtDate(s.launch_date) : "Not launched"} testid="billing-launch-date" />
        <Info label="Authorize.Net" value={s.anet?.configured ? `Connected · ${s.anet.env}` : "Keys missing"} tone={s.anet?.configured ? "ok" : "warn"} testid="billing-anet-status" />
        <Info label="Last webhook" value={s.webhook_last_event_at ? new Date(s.webhook_last_event_at).toLocaleString() : "None yet"} testid="billing-webhook-last" />
        <Info label="Renewals last run" value={s.renewals_last_run?.at ? `${new Date(s.renewals_last_run.at).toLocaleString()} · ${s.renewals_last_run.charged} charged` : "Never"} testid="billing-renewals-last" />
        <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          <span>Webhook URL: <code className="bg-white border border-slate-200 rounded px-1">{`${window.location.origin}/api${s.anet?.webhook_path?.replace("/api", "") || "/billing/webhook"}`}</code></span>
          <Button size="sm" variant="outline" onClick={runRenewals} disabled={!!busy} className="ml-auto" data-testid="billing-run-renewals">
            {busy === "renewals" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Run renewals now
          </Button>
        </div>
      </dl>

      <PlanFields form={form} setForm={setForm} />
      <FxAndMarkupFields form={form} setForm={setForm} settings={s} onRefresh={refreshFx} busy={busy === "fx"} />
      <PacksEditor packs={form.packs} onChange={(packs) => setForm({ ...form, packs })} rate={Number(form.usd_cad_rate) || 1.37} markupPct={Number(form.topup_markup_pct) || 0} unitUsd={s.pool_unit_usd || {}} />
      <TaxFields form={form} setForm={setForm} settings={s} />
      <ReceiptFields receipt={form.receipt} onChange={(receipt) => setForm({ ...form, receipt })} />

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <Button onClick={saveForm} disabled={!!busy} className="text-white min-h-[44px]" style={{ background: "hsl(var(--brand))" }} data-testid="billing-settings-save">
          {busy === "save" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save billing settings
        </Button>
        <span className="text-[11px] text-slate-500">Plan: {fmtCAD(form.plan_price_cad)}/month · {Number(form.included_enrichment || 0).toLocaleString()} enrichment + {Number(form.included_scrape || 0).toLocaleString()} scrape credits included</span>
      </div>

      <ConfirmDialog
        open={confirmLaunch}
        title="Switch billing on?"
        description="This sets today as the launch date. Every existing member gets the complimentary period from today; new members must activate before they can use paid features. Make sure the Authorize.Net keys and receipt details are filled in first."
        confirmLabel="Go live"
        onConfirm={() => { setConfirmLaunch(false); save({ billing_enabled: true }, "toggle"); }}
        onClose={() => setConfirmLaunch(false)}
        testid="billing-launch-confirm"
      />
    </section>
  );
}

function Info({ label, value, tone, testid }) {
  const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className={"text-sm font-medium mt-0.5 " + cls} data-testid={testid}>{value}</dd>
    </div>
  );
}
